import * as React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncContent } from '~/components/AsyncContent';
import { SummaryItem } from '~/components/MyWorkspace/SummaryItem';
import { useSWR } from '~/hooks/useSWR';
import {
  IndicatorCategoryType,
  IndicatorEmployeeInfo,
  IndicatorsConfig,
  fetchFromOldApi,
} from '~/services/Http';
import { IndicatorDetails } from '~/services/Http/models.people.solaintel';

export interface Props {
  indicatorId: number;
  unitId?: number;
}

export const IndicatorEmployeeInfoDialogContent: React.FC<Props> = ({
  indicatorId,
  unitId,
}) => {
  const { t } = useTranslation();
  const {
    data: indicatorDetails,
    loading,
    failedToLoad,
  } = useSWR<IndicatorDetails>(
    indicatorId && unitId
      ? ['/json/solaintel/indicator/unit', indicatorId, unitId]
      : null,
    () =>
      fetchFromOldApi(
        `/json/solaintel/indicator/${indicatorId}/unit/${unitId}/results`,
      ).then((r) => r.json()),
  );

  const getSecondaryText = useCallback(
    (
      indicatorEmployeeInfo: IndicatorEmployeeInfo,
      indicatorTypes: IndicatorCategoryType[],
      indicatorGroupCondition?: 'AND' | 'OR',
    ) => {
      if (indicatorTypes.length > 1) {
        return indicatorTypes
          .map(
            (indicatorType, index) =>
              `${t(
                IndicatorsConfig[indicatorType]
                  .employeeIndicatorInfoTranslationLabel,
              )} ${Object.values(indicatorEmployeeInfo)[index + 1]}`,
          )
          .join(` ${indicatorGroupCondition} `);
      }

      return `${t(
        IndicatorsConfig[indicatorTypes[0]]
          .employeeIndicatorInfoTranslationLabel,
      )} ${Object.values(indicatorEmployeeInfo)[1]}`;
    },
    [t],
  );

  return (
    <AsyncContent isLoading={loading} failedToLoad={failedToLoad}>
      <div data-cy="list">
        {indicatorDetails &&
          indicatorDetails.data.map((d) => (
            <SummaryItem
              key={d.employee.id}
              avatarProps={{
                alt: d.employee.firstName,
                src: d.employee.profilePic,
              }}
              primaryElement={`${d.employee.firstName} ${d.employee.lastName}`}
              secondaryElement={getSecondaryText(
                d,
                indicatorDetails.indicator.indicatorType
                  ? [indicatorDetails.indicator.indicatorType]
                  : indicatorDetails.indicator.group!.indicators.map(
                      (i) => i.indicatorType,
                    ),
                indicatorDetails.indicator.group?.condition,
              )}
              href={`/page-ui2-redirect?empId=${d.employee.id}&area=people`}
            />
          ))}
      </div>
    </AsyncContent>
  );
};
